import type { Dayjs } from 'dayjs';
import Collection from '@model-core/core/Collection';
import FileModel from '@/models/entities/FileModel';
import { TIME_SHEET_SIGNATURE_TYPE } from '@/models/references/references';
import TimeSheetSignatureModel from '@/models/services/TimeSheetSignatureModel';
import { loadReferences, useReferenceStore } from '@/stores/useReferenceOptions';

/** Une signature : quand, par qui (URN du signataire), et son URN (son image dessinée : SignatureImage) */
export interface Signature {
  at: Dayjs;
  by: string;
  urn: string;
}

/**
 * Les 3 signatures d'une feuille (encadrés de la trame, §3.1 ; export du §8) ; null pour un signataire qui n'a pas
 * signé : feuille en attente, équipe sans manager ou Dirigeant qui valide à sa place, absence pas encore signée par
 * l'employé.
 */
export interface Signatures {
  employee: Signature | null;
  manager: Signature | null;
  director: Signature | null;
}

/** @param signatures les signatures d'une feuille (TimeSheetSignature) */
export function signaturesOf(signatures: TimeSheetSignatureModel[]): Signatures {
  const of = (type: string): Signature | null => {
    const signature = signatures.find((item) => item.typeReference === type);
    return signature ? { at: signature.createdAt, by: signature.createdBy, urn: signature.urn } : null;
  };

  return {
    employee: of(TIME_SHEET_SIGNATURE_TYPE.EMPLOYEE),
    manager: of(TIME_SHEET_SIGNATURE_TYPE.MANAGER),
    director: of(TIME_SHEET_SIGNATURE_TYPE.DIRECTOR),
  };
}

/** Une requête par signataire : au plus une signature par feuille, la réponse ne dépasse pas le nombre de feuilles */
async function signaturesByType(sheetIds: string[], typeId: number) {
  const { collection } = await new Collection(TimeSheetSignatureModel)
    .setFilter('timeSheet', sheetIds as never)
    .setFilter('timeSheetSignatureType', String(typeId))
    .list();
  return collection.models;
}

/**
 * Signatures de ces feuilles : [timeSheetId => signatures], une requête par type de signature de l'API (Employé, Manager,
 * Dirigeant : liste de référence, chargée au besoin)
 */
export async function loadSignatures(sheetIds: string[]): Promise<Map<number, Signatures>> {
  await loadReferences('timeSheetSignatureTypes');
  const types = useReferenceStore.getState().lists.timeSheetSignatureTypes ?? [];
  const signatures = (await Promise.all(types.map((type) => signaturesByType(sheetIds, type.id)))).flat();

  return new Map(
    sheetIds.map((id) => [
      Number(id),
      signaturesOf(signatures.filter((signature) => signature.timeSheetId === Number(id))),
    ]),
  );
}

/**
 * Adresse de l'image dessinée d'une signature : son fichier (par l'URN de la signature), puis son adresse de
 * téléchargement temporaire. null sans image (signatures d'avant l'image, ou lecture refusée) ou pendant le chargement.
 */
export async function loadSignatureImageUrl(signatureUrn: string): Promise<string | null> {
  const { collection } = await new Collection(FileModel).setFilter('typeSource.source.entityUrn', signatureUrn).list();
  const [file] = collection.models;

  return file ? file.downloadUrl() : null;
}
